/* =============================================================================
   ics-numberbox — the numeric input field of the library. One module, two
   roles: `numberbox` (class Numberbox) and `numberspinner` (class
   Numberspinner — the numberbox plus a step). Both EXTEND ics-textbox
   (components/ics-textbox.js), so readonly / disable / enable / textbox() / option
   precedence come from the field-family core and are never re-implemented
   here. The module also defines `IcsNumber` — the library's number format /
   parse / convert definition, published as `Ics.number` (its own header below).

   AUTHORING (markup form)
     <input id="txtQty"  data-ics="numberbox" data-options="precision:2,min:0">
     <input id="txtYear" data-ics="numberbox" data-options="groupSeparator:false">
     <input id="spnCopies" data-ics="numberspinner" data-options="increment:1">
   Mount selector: <input> elements only. A grid column that names 'numberbox'
   inside an editor CONFIG STRING is not markup and is never matched.
   Facade: `$.fn.numberbox` / `$.fn.numberspinner`, installed by the bundle
   entry as WRAPPERS (see THE DISPATCH CONTRACT at the bottom of this file): a
   receiver this component owns reaches the component; a receiver it does not
   own is passed to the handler installed before it, untouched. An options
   object on an already-mounted element is an option merge (facade/register.js).

   ── THE COMMIT PIPELINE (order is load-bearing) ─────────────────────────
   One path for typed text (Enter and blur), setValue(), clear() and the
   initial value:
     strip prefix -> strip group separators -> trim -> parseFloat, which
     tolerates trailing garbage ('1.2.3' -> 1.2, '-7abc' -> -7) -> NaN / null /
     '' become '' -> clamp MAX FIRST, THEN MIN -> toFixed(precision) -> write
     onto THE ELEMENT ITSELF, so .val() already reads the committed value
     before any handler runs -> fire onChange(newString, oldString) with
     `this` = the input ONLY when the committed string actually changed, plus a
     bubbling CustomEvent 'ics:change' carrying {value, oldValue}.

   ⚠ CLAMP ORDER IS MAX-THEN-MIN: {min:20,max:10} setValue(15) yields "20"
   (min-then-max would yield "10"). Only observable when the bounds contradict.
   ⚠ onChange is SYNCHRONOUS inside setValue/commit, and the equal-value
   suppression is what TERMINATES a mutual-set cycle between two boxes that
   drive each other. Never fire on an unchanged value.
   ⚠ getValue() returns the COMMITTED value as a STRING ('' when empty).
   Callers test it against '' and read .length; a number would break them
   silently. It is the element's own value, so getValue() === .val() always.
   ⚠ An UNPARSEABLE value commits '' and is NOT clamped to min
   ({min:10} setValue('abc') -> "", not "10").
   ⚠ parseFloat's trailing-garbage tolerance is part of the contract and must
   not be "fixed".
   ⚠ A grouped string ROUND-TRIPS: setValue('1,200') -> "1200" — the group
   separators are stripped before parseFloat, so the displayed text can be
   pasted back into the field.

   ── THE VALUE IS CLEAN; THE DISPLAY IS AN OVERLAY ────────────────────────
   The authored <input> is the only field and its value is ALWAYS the bare
   formatted number — never grouped, never prefixed. Reason: callers read the
   field with .val() and wrap it in parseFloat / parseInt / Number, and
   parseFloat("1,200.50") is 1 — grouped text in the value would corrupt those
   reads silently.
   So the grouped, prefixed text lives in a sibling <span class="ics-numberbox__
   display"> painted over the input while the field is NOT focused; on focus the
   span hides and the user edits the bare number. The input's own text is made
   transparent only while the span covers it. Consequences that are FEATURES:
   .val() is clean in every state, the authored input stays VISIBLE and
   focusable (no wrapper, no hidden twin), and :input / :visible / tab order /
   $('#id').focus() all behave like a normal input.

   OPTIONS (Numberbox.defaults — each also a data-* attribute or a
   data-options="k:v" key; disabled / readonly are inherited)
     precision       decimal places, PADDED by toFixed                    (0)
     min, max        bounds applied at commit; null / '' = no bound       (null)
     prefix          text before the number in the DISPLAY only ('$ ')   ('')
     groupSeparator  the thousands character in the DISPLAY. `false`
                     renders the number bare — set it on a field whose
                     number is not a quantity (a year, a document number),
                     or 2026 renders as 2,026                             (',')
     value           the initial value; else the element's `value`
                     attribute, else its live .value                      ('')
     onChange        fn(newString, oldString), `this` = the input element (null)
     selectOnFocus   select the whole text on focus                       (false)
   Not implemented (no such option exists): suffix, formatter, parser,
   decimalSeparator.
   Set on mount, never over an authored value: `inputmode` — 'decimal' when
   precision > 0, else 'numeric' (the soft keyboard on touch devices) — and
   removed again on destroy when the component added it.

   METHODS (instance; the facade dispatches method strings): setValue(v) ·
   getValue() · clear() · disable() · enable() · plus the inherited field
   methods (readonly, textbox, option, options). Numberspinner adds spin(up).

   KEYBOARD: digits always; ONE leading '-' and only where min allows a
   negative; ONE '.' and only when precision > 0; both judged against the text
   the key will LEAVE (a selected run is replaced, so a dot inside the selection
   is not a second dot); control keys and shortcuts untouched; Enter commits IN
   PLACE and never submits the form. Paste and IME are NOT filtered — the commit
   pass cleans them. There is deliberately no type-time min/max gate (it would
   refuse legal intermediate states — min:10 would block the '1' of '15');
   bounds apply at commit.
   ⚠ `precision > 0` IS THE DECLARATION THE DOT NEEDS. The default is 0 (the
   EasyUI default the estate inherited), so a field that holds a double /
   decimal business-object property MUST author `precision:N` — 2 by default
   (owner ruling 2026-09-02), else the screen's stated count. A field that
   .val()s "12.50" into a precision-0 box paints the decimals (a plain write is
   never normalised) and then refuses the dot and rounds on blur: that is the
   FIELD's declaration missing, not this rule. The estate pass that wrote the
   declarations from the BO types is `_UIUpgrade\tools\numberbox-precision-census.node.mjs`.

   STYLING: the family classes (.ics-textbox + the state hooks) plus the
   overlay part in css/components/ics-textbox.css. A numberbox reads RIGHT by
   default (`input[data-ics="numberbox"]`, weight (0,1,0) — an authored inline
   style or any page rule still wins; the numberspinner role is not aligned,
   its chips sit on the right edge).

   ── THE SINGLE DEFINITION ────────────────────────────────────────────────
   This class is THE one numeric definition in the product. The value
   semantics are PURE STATICS (parseNumber / clampNumber / commitString /
   formatDisplay + the option resolvers optionOf / resolvePrecision /
   resolveGroupSeparator); the instance pipeline delegates to them, and any
   OTHER code that needs numeric semantics (a grid inline editor, a future
   consumer) resolves this class AT RUNTIME — `Ics.components.Numberbox` — and
   calls the statics.
   ⚠ NEVER import this module into another bundle: the import's top-level
   Registry.add would re-register the component there and double-mount every
   numberbox on the page.
   ============================================================================= */
import { Textbox } from './ics-textbox.js';
import { Registry } from '../services/registry.js';
import { isOn } from '../core/options.js';   // the option-boolean gate: ONE copy for the library, never a local copy

/* an option that was never set: null / undefined / '' all mean "no bound" */
const unset = (v) => v === null || v === undefined || v === '';

/* =============================================================================
   Ics.number — THE number FORMAT / PARSE / CONVERT definition of the product.

   Published as `Ics.number` by index.textbox.js. It is the ONLY number
   formatter in the product: grid cell formatters, report figures, field
   displays, unit conversions and amounts in words all go through this object.
   The option set of `format` is a UNION of the output shapes the product
   needs, each option named after the behaviour it selects. The house DEFAULTS
   (a bare `Ics.number.format(v)`) are the clean ones — 2 dp, grouped, 0 and
   null render "0.00", an empty string renders "". A caller that depends on a
   specific shape passes its options EXPLICITLY; never rely on a default for a
   load-bearing output.

   THE OPTIONS (format):
     precision        dp for toFixed, PADDED                                (2)
     maxPrecision     round to AT MOST n dp, NEVER pad; overrides precision (null)
     minPrecision     with maxPrecision: pad to at least n dp (the
                      toLocaleString minimumFractionDigits behaviour)       (0)
     nullAs           null/undefined -> 'zero' (format 0) | 'blank' |
                      'NaN' (the literal, prefixed)                         ('zero')
     blankIfLooseEmpty a loose `v == ''` test BEFORE parsing: '', 0 and
                      false render '' ('0' and null do NOT match it)        (false)
     blankZero        a zero renders '': true | 'parsed' test the value as
                      READ (0.005 is not zero, so it formats); 'rounded'
                      tests it AFTER toFixed (0.004 at 2 dp renders '')    (false)
     nanAs            unparseable -> 'blank' | 'NaN' (the literal) | 'zero' ('blank')
     trimZeroDecimals '1,234.00' -> '1,234' (an all-zero fraction is dropped) (false)
     prefix           text before the number ('$ ', 'TK ')                  ('')
     grouping         'western' 1,234,567 | 'indian' 12,34,567 | 'none'    ('western')
     groupSeparator   the group character; '' = no grouping                 (',')
     coerce           how a non-number input is read: 'parseFloat' |
                      'Number' (Number('') = 0, Number('1,234') = NaN)      ('parseFloat')
   ⚠ format() coerces the RAW input: parseFloat('1,234.5') is 1 and
   Number('1,234') is NaN — a grouped STRING is NOT re-read by format(). Read
   a grouped string with parseRaw / parse first.
   BOUNDARY RULES:
     · `fixed()` stringifies a NUMBER input and reads undefined as null.
     · `exact()` returns the STRING '0' for null.
   THE BANGLA MEMBERS:
     · `toWordsBn(n)` — Bangla words (কোটি / লক্ষ / হাজার / শত), PURE words like
       `toWords`: an amount suffix (' টাকা মাত্র.' / ' Taka Only.') is the
       caller's to append.
     · `toBanglaDigits(v)` — '0'..'9' -> '০'..'৯' in any string, every other
       character untouched; null/undefined -> ''.
   NEVER import this module into another bundle for these: resolve
   `globalThis.Ics.number` at call time (see the header above).
   ============================================================================= */

/** the loose `v == ''` test: true for '', 0, false, [] */
const _looseEmpty = (v) => v == '';                                      // eslint-disable-line eqeqeq

/** group the INTEGER part of a numeric string in threes; the rest is untouched */
function _groupWestern(intPart, gs) {
  let x1 = intPart;
  const process = /(\d+)(\d{3})/;
  while (process.test(x1)) x1 = x1.replace(process, '$1' + gs + '$2');
  return x1;
}

/** 12,34,567 — the Indian (en-IN) scheme: the last three digits, then pairs */
function _groupIndian(intPart, gs) {
  const m = intPart.match(/^(-?)(\d+)(.*)$/);
  if (!m) return intPart;
  const sign = m[1], digits = m[2], tail = m[3];
  if (digits.length <= 3) return intPart;
  const last3 = digits.slice(-3);
  let rest = digits.slice(0, -3);
  rest = rest.replace(/\B(?=(\d{2})+(?!\d))/g, gs);
  return sign + rest + gs + last3 + tail;
}

/** toFixed that behaves like toLocaleString at the edges: -0 keeps its sign and a
    magnitude >= 1e21 is written out in full instead of "1e+21" */
function _fixedExpanded(r, dp) {
  if (!isFinite(r)) return String(r);
  const neg = r < 0 || Object.is(r, -0);
  const a = Math.abs(r);
  let s;
  if (a >= 1e21) s = BigInt(Math.trunc(a)).toString() + (dp ? '.' + '0'.repeat(dp) : '');
  else s = a.toFixed(dp);
  return (neg ? '-' : '') + s;
}
/** strip trailing zeros down to `min` decimals ("0.300" -> "0.30"; "5.00" -> "5" at min 0) */
function _trimDecimals(s, min) {
  const dot = s.indexOf('.');
  if (dot < 0) return s;
  let end = s.length;
  while (end > dot + 1 + min && s.charAt(end - 1) === '0') end--;
  if (end === dot + 1) end = dot;                  // no decimals left: drop the point
  return s.slice(0, end);
}

/** digits after the decimal point of a number; 0 for an integer or a non-finite value */
function _countDecimals(value) {
  const n = Number(value);
  if (!isFinite(n)) return 0;
  if (Math.floor(n) === n) return 0;
  const str = Math.abs(n).toString();
  const dot = str.indexOf('.');
  return dot === -1 ? 0 : str.length - dot - 1;
}

const FORMAT_DEFAULTS = Object.freeze({
  precision: 2, maxPrecision: null, minPrecision: 0,
  nullAs: 'zero', blankIfLooseEmpty: false, blankZero: false, nanAs: 'blank',
  trimZeroDecimals: false, prefix: '', grouping: 'western', groupSeparator: ',',
  coerce: 'parseFloat',        // | 'Number' - how a non-number input is read (see format)
});

export const IcsNumber = {
  /** the option defaults, exposed so a gate can pin them */
  defaults: FORMAT_DEFAULTS,

  /** group a NUMERIC STRING's integer part; decimals, sign and non-digits untouched.
      o: {groupSeparator (','), grouping ('western' | 'indian' | 'none')} */
  group(str, o) {
    const gs = (o && o.groupSeparator != null) ? String(o.groupSeparator) : ',';
    const grouping = (o && o.grouping) || 'western';
    const s = String(str);                          // String() coercion: null / undefined print as "null" / "undefined"
    if (grouping === 'none' || gs === '') return s;
    const dot = s.indexOf('.');
    const int = dot < 0 ? s : s.slice(0, dot);
    const frac = dot < 0 ? '' : s.slice(dot);
    return (grouping === 'indian' ? _groupIndian(int, gs) : _groupWestern(int, gs)) + frac;
  },

  /**
   * text|number -> number|null, the FIELD's parse: strip `prefix` and the group
   * separator, trim, then parseFloat (trailing garbage tolerated). null for
   * nothing parseable. Accounting parens read as a negative ('(1,234)' ->
   * -1234) unless o.parens === false.
   */
  parseRaw(raw, o) {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'number') return isFinite(raw) ? raw : null;
    let s = String(raw);
    const p = o && o.prefix;
    if (p) s = s.split(p).join('');
    const gs = (o && o.groupSeparator != null) ? String(o.groupSeparator) : ',';
    if (gs) s = s.split(gs).join('');
    s = s.trim();
    let neg = false;
    /* accounting parens read as a NEGATIVE by default (the grid summers need
       that) but NOT for the field, which commits '' for '(12)' and therefore
       passes parens:false */
    if ((!o || o.parens !== false) && /^\(.*\)$/.test(s)) { neg = true; s = s.slice(1, -1).trim(); }
    if (s === '') return null;
    const n = parseFloat(s);
    return isNaN(n) ? null : (neg ? -n : n);
  },

  /** the house number read: a NUMBER always - 0 for empty / unparseable */
  parse(v, o) { const n = this.parseRaw(v, o); return n === null ? 0 : n; },

  /** the LOOSE read the grid summers use: every character but digits, '.' and '-' is
      dropped first, so a formatted or currency-bearing cell ("TK 1,234.50") reads as
      1234.5; ''/null/NaN are 0 */
  parseLoose(value) {
    if (value === undefined || value === null || value === '') return 0;
    if (typeof value === 'number') return isNaN(value) ? 0 : value;
    const n = parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? 0 : n;
  },

  /** `parseFloat` over an UN-GROUPED string — what every screen-local
      `parseFloat(x.replace(/,/g, ''))` meant: the group separator (and a prefix,
      when given) stripped, then parseFloat's own contract — NaN for nothing
      parseable (null, undefined, '', 'abc'), trailing garbage tolerated, accounting
      parens NOT a negative (parseFloat never read them). Use `parse` when an
      unreadable value should be 0 instead. */
  toFloat(v, o) {
    const n = this.parseRaw(v, Object.assign({ parens: false }, o || {}));
    return n === null ? NaN : n;
  },

  /** a decimal-place count from anything (a string, null, NaN) — `fallback` (2)
      when unusable */
  dp(v, fallback) {
    const f = fallback == null ? 2 : fallback;
    if (v === undefined || v === null) return f;
    const n = parseFloat(v);
    return isNaN(n) ? f : parseInt(v, 10);
  },

  /** numeric test: !isNaN(parseFloat(v)) && isFinite(v) */
  isNumeric(v) { return !isNaN(parseFloat(v)) && isFinite(v); },

  /** round to p dp and return a NUMBER; NaN -> 0 */
  round(v, p) {
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (isNaN(n)) return 0;
    const f = Math.pow(10, p == null ? 0 : p);
    return Math.round(n * f) / f;
  },

  /**
   * fixed(userInput, dpoint[, o]): keep digits, '-' and '.', skip ',', DROP
   * everything else (a currency prefix, accounting parens — the sign is lost
   * there), ''/null/undefined -> '0.00', then a STRING via toFixed(dpoint ?? 6);
   * an unparseable residue is the literal 'NaN' unless o.nanAs === 'zero'.
   * A NUMBER input is stringified first. o.keepSign === false drops the '-' too.
   */
  fixed(userInput, dpoint, o) {
    const keepSign = !o || o.keepSign !== false;    // keepSign:false drops the '-' as well
    let s;
    if (userInput === null || userInput === undefined || userInput === '') s = '0.00';
    else {
      s = '';
      const str = String(userInput);
      for (let i = 0; i < str.length; i++) {
        const ch = str.charAt(i);
        if (/\d/.test(ch) || (keepSign && ch === '-') || ch === '.') s += ch;
      }
    }
    let dp = 6;
    if (dpoint !== undefined && dpoint !== null) dp = parseInt(dpoint, 10);
    let n = parseFloat(s);
    if (isNaN(n) && o && o.nanAs === 'zero') n = 0;   // nanAs:'zero' — an unreadable residue is 0, not 'NaN'
    return n.toFixed(dp);
  },

  /** the value's own string with trailing zeros stripped, NO grouping, NO rounding
      ('1.2500' -> '1.25', '3.000' -> '3'); null/undefined -> '0' */
  exact(v) {
    if (v === null || v === undefined) return '0';
    let s = String(v);
    return s.includes('.') ? s.replace(/\.?0*$/, '') : s;
  },

  /**
   * THE format. See the option table in the header. The branches run in a FIXED
   * order — null -> loose-empty -> parse -> zero -> round -> trim -> group ->
   * prefix — and that order is part of the contract.
   * @returns {string}
   */
  format(v, opts) {
    const o = opts ? Object.assign({}, FORMAT_DEFAULTS, opts) : FORMAT_DEFAULTS;
    if (v === null || v === undefined) {
      if (o.nullAs === 'blank') return '';
      if (o.nullAs === 'NaN') return (o.prefix || '') + 'NaN';   // nullAs:'NaN' prints the literal
      v = 0;
    }
    if (o.blankIfLooseEmpty && _looseEmpty(v)) return '';
    /* ⚠ RAW coercion: parseFloat('1,234.5') is 1 and Number('1,234') is NaN — a
       grouped STRING is NOT re-read here (the FIELD strips separators on its own
       parse path; format() does not).
       coerce:'Number' has toLocaleString's input semantics (Number('') = 0). */
    const n = typeof v === 'number' ? v : (o.coerce === 'Number' ? Number(v) : parseFloat(v));
    if (isNaN(n)) {
      if (o.nanAs === 'NaN') return o.prefix + 'NaN';
      if (o.nanAs === 'zero') return this.format(0, Object.assign({}, o, { nanAs: 'blank' }));
      return '';
    }
    /* blankZero has TWO readings: 'parsed' (or true) blanks only a value that IS
       zero as read, so 0.005 is formatted, not blanked; 'rounded' blanks a value
       whose toFixed result is zero, so 0.004 at 2 dp renders ''. */
    if ((o.blankZero === true || o.blankZero === 'parsed') && n == 0) return '';   // eslint-disable-line eqeqeq
    let body;
    if (o.maxPrecision !== null && o.maxPrecision !== undefined) {
      if (o.minPrecision || o.coerce === 'Number') {
        /* the toLocaleString shape (minPrecision set, or coerce:'Number'): round to
           maxPrecision, then trim trailing zeros down to minPrecision; -0 keeps its
           sign; >= 1e21 is written out; a magnitude past 2^53 has no decimals to round */
        const mult = Math.pow(10, o.maxPrecision);
        let r = Math.abs(n) >= 1e15 ? n : Math.round(n * mult + Number.EPSILON) / mult;
        if (Object.is(n, -0)) r = -0;                // Math.round(-0 + ε) is +0; Intl prints "-0"
        body = _trimDecimals(_fixedExpanded(r, o.maxPrecision), o.minPrecision || 0);
      } else {
        /* the maxPrecision-only shape: round at the value's OWN decimal count when
           that is below maxPrecision, with a floor of 2 digits — the `digits < 2`
           arm always multiplies by 100, and its float artifact at 1e21
           ("999,999,999,999,999,900,000") is the contracted output */
        const digits = _countDecimals(n);
        let mult;
        if (digits >= o.maxPrecision) mult = Math.pow(10, o.maxPrecision);
        else if (digits < 2) mult = 100;
        else mult = Math.pow(10, digits);
        const r = Math.round(n * mult + Number.EPSILON) / mult;
        body = String(r);                          // String(-0) is "0": this shape never prints a signed zero
      }
    } else {
      body = o.coerce === 'Number' ? _fixedExpanded(n, o.precision) : n.toFixed(o.precision);
    }
    if (o.blankZero === 'rounded' && parseFloat(body) == 0) return '';   // eslint-disable-line eqeqeq
    if (o.trimZeroDecimals) {
      const parts = body.split('.');
      if (parts.length > 1 && parseInt(parts[1], 10) <= 0) body = parts[0];
    }
    return (o.prefix || '') + this.group(body, o);
  },

  /** a grid column valueFormatter over `format`: fn(params) formats params.value —
      `valueFormatter: Ics.number.cellFormatter({...})` */
  cellFormatter(opts) {
    const self = this;
    return function (params) { return self.format(params ? params.value : null, opts); };
  },

  /** a grid column valueParser: fn(params) reads params.newValue through `parse`
      (unparseable -> 0) */
  cellParser() {
    const self = this;
    return function (params) { return self.parse(params ? params.newValue : null); };
  },

  /* ---- unit converters: a STRING via toFixed(digits), digits capped at 8; an
     undefined digit count means toFixed(0) ---- */
  toKg(lbs, digits) { if (digits > 8) digits = 8; return (lbs * 0.45359237001003542909395360718511).toFixed(digits); },
  toLbs(kg, digits) { if (digits > 8) digits = 8; return (kg * 2.2046226218).toFixed(digits); },
  toMeter(yard, digits) {
    if (digits > 8) digits = 8;
    let m = parseFloat(yard) / parseFloat(1.0936132983);
    if (isNaN(m)) m = 0; else if (m == '' || m == null) m = 0;             // eslint-disable-line eqeqeq
    return m.toFixed(digits);
  },
  toYard(meter, digits) {
    if (digits > 8) digits = 8;
    let y = parseFloat(meter) * parseFloat(1.0936132983);
    if (isNaN(y)) y = 0; else if (y == '' || y == null) y = 0;             // eslint-disable-line eqeqeq
    return y.toFixed(digits);
  },

  /* ---- amount in words: ONE engine, Indian grouping (Crore / Lakh / Thousand),
     standard spellings, no hyphen ("Twenty One"); out of range -> the sentinel
     'Number is out of range' ---- */
  toWords(number) {
    number = Math.floor(Number(number) || 0);
    if (number < 0 || number > 999999999) return 'Number is out of range';
    if (number === 0) return 'Zero';
    const crore = Math.floor(number / 10000000); number %= 10000000;
    const lakh = Math.floor(number / 100000); number %= 100000;
    const thousand = Math.floor(number / 1000); number %= 1000;
    const parts = [];
    if (crore) parts.push(this.toWords(crore) + ' Crore');
    if (lakh) parts.push(_hundredsWords(lakh) + ' Lakh');
    if (thousand) parts.push(_hundredsWords(thousand) + ' Thousand');
    if (number) parts.push(_hundredsWords(number));
    return parts.join(' ');
  },
  /** "<words> TAKA and <words> Paisa Only." (a dollar currency -> Cent) */
  amountInWords(number, sCurrency) {
    sCurrency = sCurrency == null ? '' : String(sCurrency).trim();
    const lc = sCurrency.toLowerCase();
    let note = '', minor = '';
    if (lc == 'taka' || lc == 'tk' || lc == 'bdt' || sCurrency == '') { note = sCurrency.toUpperCase(); minor = 'Paisa'; }   // eslint-disable-line eqeqeq
    else if (lc == '$' || lc == 'dollar' || lc == 'usd') { note = sCurrency.toUpperCase(); minor = 'Cent'; }                // eslint-disable-line eqeqeq
    if (parseInt(String(number).indexOf('.'), 10) > -1) {
      const sNumber = Number(number).toFixed(2).split('.');
      return this.toWords(parseInt(sNumber[0], 10)) + ' ' + note + ' and ' + this.toWords(parseInt(sNumber[1], 10)) + ' ' + minor + ' Only.';
    }
    return this.toWords(parseInt(number, 10)) + ' ' + note + ' Only.';
  },
  /** "<before> <words> And <after> <words> Only" ('Minus' prefixed on negatives) */
  inWords(inputValue, beforeDecimal, afterDecimal) {
    let sign = '';
    let v = Number(inputValue) || 0;
    if (v < 0) { sign = 'Minus'; v = Math.abs(v); }
    const [whole, frac] = v.toFixed(2).split('.');
    let taka = this.toWords(parseInt(whole, 10));
    taka = taka === 'Zero' ? '' : beforeDecimal + ' ' + taka;
    const paisa = parseInt(frac, 10) ? afterDecimal + ' ' + this.toWords(parseInt(frac, 10)) : '';
    let out = taka;
    if (out != '' && paisa != '') out += ' And ';                            // eslint-disable-line eqeqeq
    out += paisa;
    if (out == '') out = beforeDecimal + ' Zero';                            // eslint-disable-line eqeqeq
    return sign + out + ' Only';
  },
  /** '' for a falsy amount, else inWords(v, 'Taka', 'Paisa') */
  takaWords(inputValue) {
    if (!inputValue || inputValue === '' || inputValue === 0) return '';
    return this.inWords(inputValue, 'Taka', 'Paisa');
  },

  /* ---- the Bangla members ------------------------------------------------ */
  /** Bangla words, Indian grouping (কোটি / লক্ষ / হাজার / শত). The input is
      FLOORED first (a fraction never reaches the word table); the range guard
      and the 'Number is out of range' sentinel are `toWords`'s, so the two
      engines answer alike. PURE words: 0 -> 'শূন্য', no currency suffix. */
  toWordsBn(number) {
    number = Math.floor(Number(number) || 0);
    if (number < 0 || number > 999999999) return 'Number is out of range';
    if (number === 0) return BN_WORDS_ZERO;
    const koti = Math.floor(number / 10000000); number %= 10000000;
    const lakh = Math.floor(number / 100000); number %= 100000;
    const hajar = Math.floor(number / 1000); number %= 1000;
    const parts = [];
    if (koti) parts.push(_hundredsWordsBn(koti) + ' কোটি');
    if (lakh) parts.push(_hundredsWordsBn(lakh) + ' লক্ষ');
    if (hajar) parts.push(_hundredsWordsBn(hajar) + ' হাজার');
    if (number) parts.push(_hundredsWordsBn(number));
    return parts.join(' ');
  },
  /** '0'..'9' -> '০'..'৯' in the string form of `v`; every other character (a
      separator, a sign, a letter, a Bangla digit already) is untouched.
      null/undefined -> ''. */
  toBanglaDigits(v) {
    if (v === null || v === undefined) return '';
    return String(v).replace(/[0-9]/g, (d) => BN_DIGITS[d]);
  },
};

const BN_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
const BN_WORDS_ZERO = 'শূন্য';
/* 0..99 */
const BN_WORDS = ['', 'এক', 'দুই', 'তিন', 'চার', 'পাঁচ', 'ছয়', 'সাত', 'আট', 'নয়',
  'দশ', 'এগারো', 'বারো', 'তেরো', 'চৌদ্দ', 'পনেরো', 'ষোল', 'সতেরো', 'আঠারো', 'উনিশ',
  'বিশ', 'একুশ', 'বাইশ', 'তেইশ', 'চব্বিশ', 'পঁচিশ', 'ছাব্বিশ', 'সাতাশ', 'আটাশ', 'ঊনত্রিশ',
  'ত্রিশ', 'একত্রিশ', 'বত্রিশ', 'তেত্রিশ', 'চৌত্রিশ', 'পঁয়ত্রিশ', 'ছত্রিশ', 'সাঁইত্রিশ', 'আটত্রিশ', 'ঊনচল্লিশ',
  'চল্লিশ', 'একচল্লিশ', 'বিয়াল্লিশ', 'তেতাল্লিশ', 'চুয়াল্লিশ', 'পঁয়তাল্লিশ', 'ছেচাল্লিশ', 'সাতচল্লিশ', 'আটচল্লিশ', 'ঊনপঞ্চাশ',
  'পঞ্চাশ', 'একান্ন', 'বায়ান্ন', 'তিপ্পান্ন', 'চুয়ান্ন', 'পঞ্চান্ন', 'ছাপ্পান্ন', 'সাতান্ন', 'আটান্ন', 'ঊনষাট',
  'ষাট', 'একষট্টি', 'বাষট্টি', 'তেষট্টি', 'চৌষট্টি', 'পঁয়ষট্টি', 'ছেষট্টি', 'সাতষট্টি', 'আটষট্টি', 'ঊনসত্তর',
  'সত্তর', 'একাত্তর', 'বাহাত্তর', 'তিয়াত্তর', 'চুয়াত্তর', 'পঁচাত্তর', 'ছিয়াত্তর', 'সাতাত্তর', 'আটাত্তর', 'ঊনআশি',
  'আশি', 'একাশি', 'বিরাশি', 'তিরাশি', 'চুরাশি', 'পঁচাশি', 'ছিয়াশি', 'সাতাশি', 'আটাশি', 'ঊননব্বই',
  'নব্বই', 'একানব্বই', 'বিরানব্বই', 'তিরানব্বই', 'চুরানব্বই', 'পঁচানব্বই', 'ছিয়ানব্বই', 'সাতানব্বই', 'আটানব্বই', 'নিরানব্বই'];
/** 0..999 -> Bangla words ('' for 0): 0..99 from the table, hundreds as '<n> শত' */
function _hundredsWordsBn(n) {
  n = Math.floor(Math.abs(n)) % 1000;
  if (n < 100) return BN_WORDS[n];
  return BN_WORDS[Math.floor(n / 100)] + ' শত' + (n % 100 ? ' ' + BN_WORDS[n % 100] : '');
}

const ONES_WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve',
  'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS_WORDS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
/** 0..999 -> words ('' for 0) */
function _hundredsWords(n) {
  n = Math.floor(Math.abs(n)) % 1000;
  const parts = [];
  if (n >= 100) { parts.push(ONES_WORDS[Math.floor(n / 100)] + ' Hundred'); n = n % 100; }
  if (n >= 20) { parts.push(TENS_WORDS[Math.floor(n / 10)]); if (n % 10) parts.push(ONES_WORDS[n % 10]); }
  else if (n > 0) parts.push(ONES_WORDS[n]);
  return parts.join(' ');
}

export class Numberbox extends Textbox {
  /* the base keys are INHERITED by spreading Textbox.defaults, never hand-copied:
     resolveOptions builds from `this.defaults`, so a pasted
     `disabled:false, readonly:false` would freeze today's base keys, and a key
     later added to Textbox.defaults would silently never be read from a
     numberbox's data- attributes */
  static defaults = Object.assign({}, Textbox.defaults, {
    min: null, max: null, precision: 0,
    prefix: '', groupSeparator: ',',
    value: '', onChange: null,
    selectOnFocus: false,      // select the whole text on focus; off by default
  });

  /**
   * The inherited resolver (defaults <- native attrs <- data-* <- ctor) plus the
   * rendered value: a server-rendered `value="17"` attribute, or a live .value a
   * script already wrote, is the field's initial number
   * (`<input value="17">` + precision:2 -> "17.00").
   */
  static resolveOptions(el, options) {
    const o = super.resolveOptions(el, options);
    if (unset(o.value) && el && el.getAttribute) {
      const av = el.getAttribute('value');
      if (av != null && av !== '') o.value = av;
      else if (el.value) o.value = el.value;
    }
    return o;
  }

  /* ── THE SINGLE NUMERIC DEFINITION — pure statics ─────────────────────────
     No DOM, no instance, no globals: (raw, options) in, value out. The instance
     pipeline below delegates here, and every static calls its siblings through
     `this` (= the class), so a subclass that overrides one static is honoured
     by the whole pipeline. External consumers resolve the class at runtime
     (`Ics.components.Numberbox`) — see the header for why never by import. */

  /**
   * ⚠ A BARE BAG MUST ANSWER LIKE A MOUNTED FIELD. A mounted field's options
   * always carry the class defaults, so `{precision:2}` on an instance still
   * groups on ','. The statics get raw bags from outside callers, where an
   * ABSENT key must mean "the default" and never "opted out" — reading an absent
   * groupSeparator as '' would make commitString('1,234.5', {precision:2})
   * answer "1.00". So every static reads through this: the caller's key when
   * PRESENT (even as false/null — that is the opt-out), the class default
   * otherwise. `this` is the class when called on it (subclass defaults
   * honoured); a detached call falls back to Numberbox.
   */
  static optionOf(o, key) {
    if (o && Object.prototype.hasOwnProperty.call(o, key)) return o[key];
    const D = (this && this.defaults) || Numberbox.defaults;
    return D[key];
  }

  /** precision as a sane integer; anything unusable means 0 */
  static resolvePrecision(o) {
    const p = parseInt(this.optionOf(o, 'precision'), 10);
    return isNaN(p) || p < 0 ? 0 : p;
  }

  /** the group separator, or '' when the field opted out with groupSeparator:false */
  static resolveGroupSeparator(o) {
    const g = this.optionOf(o, 'groupSeparator');
    if (g === false || g === 'false' || g == null) return '';
    return String(g);
  }

  /**
   * text -> number|null. Strips the prefix and the group separators FIRST, so a
   * grouped string round-trips, then defers to parseFloat, whose
   * trailing-garbage tolerance is part of the contract and must not be "fixed".
   */
  static parseNumber(raw, o) {
    /* ONE parser: IcsNumber.parseRaw, with the field's resolved prefix/separator
       and parens OFF (the field commits '' for '(12)') */
    return IcsNumber.parseRaw(raw, { prefix: this.optionOf(o, 'prefix'), groupSeparator: this.resolveGroupSeparator(o), parens: false });
  }

  /** MAX FIRST, THEN MIN — contradictory bounds resolve to min */
  static clampNumber(n, o) {
    const max = this.optionOf(o, 'max'), min = this.optionOf(o, 'min');
    if (!unset(max)) { const mx = parseFloat(max); if (!isNaN(mx) && n > mx) n = mx; }
    if (!unset(min)) { const mn = parseFloat(min); if (!isNaN(mn) && n < mn) n = mn; }
    return n;
  }

  /** the WHOLE commit pipeline as a value: parse -> clamp -> toFixed, '' for unparseable */
  static commitString(raw, o) {
    const n = this.parseNumber(raw, o);
    return n === null ? '' : this.clampNumber(n, o).toFixed(this.resolvePrecision(o));
  }

  /** committed string -> what the user reads: '1200.5' -> '1,200.50' + prefix.
      Grouping touches the INTEGER part only, and never the sign or the decimals. */
  static formatDisplay(committed, o) {
    const v = committed == null ? '' : String(committed);
    if (v === '') return '';
    /* ONE grouper: IcsNumber.group — integer part only, sign and decimals
       untouched; '' separator = the field opted out */
    return (this.optionOf(o, 'prefix') || '') + IcsNumber.group(v, { groupSeparator: this.resolveGroupSeparator(o) });
  }

  _init() {
    super._init();                       // ics-textbox class + disabled/readonly, from the family core
    this.el.classList.add('ics-numberbox');
    this._takeOnChange();
    this._syncInputmode();                    // the mobile keyboard hint - only when the view authored none
    this._buildDisplay();
    this._watchValue();                       // a plain `.val()` write must repaint too - see _watchValue
    this._bindNumeric();
    this._commit(this.options.value, true);   // the initial value never ripples: no onChange at init
    this._paint();
  }

  /**
   * ⚠ Component.emit() ALREADY invokes the matching `on<Type>` option, so a
   * component that also calls options.onChange itself fires it TWICE - and the
   * second call arrives with emit's payload shape ({value, oldValue}) and `this`
   * bound to the component. This component's onChange contract is
   * onChange(newValue, oldValue) as two STRINGS with `this` = the input element.
   * So the option is taken off the bag here and invoked by _commit in that
   * shape, leaving emit to do only what it should - the bubbling CustomEvent
   * 'ics:change' carrying the family payload.
   */
  _takeOnChange() {
    if (typeof this.options.onChange === 'function') this._onChangeFn = this.options.onChange;
    this.options.onChange = null;
  }

  /**
   * `inputmode` steers the soft keyboard on touch devices.
   * The VIEW's own attribute always wins (authored = the screen's choice, and
   * `_inputmodeAuthored` is decided ONCE, before we ever write); otherwise
   * decimal fields get the dot keyboard and whole-number fields the digit pad.
   */
  _syncInputmode() {
    const el = this.el;
    if (!el || typeof el.setAttribute !== 'function' || typeof el.hasAttribute !== 'function') return;   // a bare element stub with no attribute API: nothing to set
    if (this._inputmodeAuthored === undefined) this._inputmodeAuthored = el.hasAttribute('inputmode');
    if (this._inputmodeAuthored) return;
    el.setAttribute('inputmode', this._precision() > 0 ? 'decimal' : 'numeric');
  }

  /* ---- the value pipeline (instance face of the statics above) ------------ */

  _precision() { return this.constructor.resolvePrecision(this.options); }

  _gs() { return this.constructor.resolveGroupSeparator(this.options); }

  _parse(raw) { return this.constructor.parseNumber(raw, this.options); }

  _clamp(n) { return this.constructor.clampNumber(n, this.options); }

  /**
   * THE one write path. `silent` suppresses the change notification (initial
   * value and re-formatting under new options); everything else fires exactly
   * once, and only on a real change.
   * @returns {string} the committed value
   */
  _commit(raw, silent) {
    const old = this.el.value;
    const next = this.constructor.commitString(raw, this.options);   // THE pipeline, defined once above
    this._writing = true;                     // this path paints for itself - see _watchValue
    try { this.el.value = next; } finally { this._writing = false; }
    this._paint();
    if (!silent && next !== old) {
      /* the callback first: two STRINGS, `this` = the input element. A
         throwing handler is logged and never breaks the commit or the event. */
      if (typeof this._onChangeFn === 'function') {
        try { this._onChangeFn.call(this.el, next, old); } catch (e) { console.error('[ics] numberbox onChange failed', e); }
      }
      this.emit('change', { value: next, oldValue: old });   // options.onChange is null here by design - see _takeOnChange
    }
    return next;
  }

  /** the committed value, as a STRING - identical to $el.val() by construction */
  getValue() { return this.el.value; }

  setValue(value) { this._commit(value, false); return this; }

  clear() { this._commit('', false); return this; }

  /* ---- the display overlay ------------------------------------------------ */

  /** what the user reads while blurred - the static, fed the committed value */
  _display() { return this.constructor.formatDisplay(this.el.value, this.options); }

  /**
   * The overlay is a SIBLING, never a wrapper: the field family never wraps the
   * authored input (a wrapper breaks .val(), :input and exact-class matching on
   * it). It is positioned against the input's offset box, so the input's parent
   * may need to become a containing block - recorded and reverted on destroy.
   */
  _buildDisplay() {
    const el = this.el;
    const host = el.parentNode;
    if (!host || host.nodeType !== 1) return;
    const cs = globalThis.getComputedStyle ? getComputedStyle(host) : null;
    if (cs && cs.position === 'static') { host.style.position = 'relative'; this._hostPositioned = host; }
    const span = document.createElement('span');
    span.className = 'ics-numberbox__display';
    span.setAttribute('aria-hidden', 'true');       // the input carries the accessible value
    this._display$ = span;
    host.insertBefore(span, el.nextSibling);
    this._syncDisplayBox();
    this._watchBox(host);
  }

  /**
   * ⚠ THE OVERLAY BOX IS MEASURED, SO IT MUST BE RE-MEASURED.
   * _syncDisplayBox reads offsetLeft/offsetTop/offsetWidth, and inside a
   * `display:none` host every one of them is 0 - so a field that mounts and
   * commits while hidden paints a 0-width overlay, and nothing re-runs when the
   * host is finally shown. That is a NORMAL load order, not an edge case: fields
   * sit in tab pages, windows and collection dialogs that are hidden at ready
   * time and are setValue()d from a server payload before being shown.
   * A ResizeObserver on the input is the exact signal - it fires when the element
   * gains a box (none -> shown) and on every later resize. The HOST is observed
   * too, because a fixed-width input can be MOVED by its container growing
   * without changing size itself. The observer is handed to the EventBinder, so
   * destroy() disconnects it with everything else.
   */
  _watchBox(host) {
    /* ⚠ A RESIZE OBSERVER SEES SIZE, NOT POSITION. A toolbar is an inline row - a
       label, a textbox, this field, a combo, buttons - and sibling components mount
       from their own bundles AFTER this one. When they do, the row reflows and this
       input MOVES without changing size, so no observer fires and the overlay stays
       at the coordinates it was given (a whole line up and hundreds of pixels
       along, in a reflowing toolbar). So the box is re-measured again once the
       mount storm has settled: the next frame, and once more shortly after. Both
       are EventBinder-tracked, so a field destroyed in between cancels them. */
    this._events.timeout(() => { if (!this._editing) this._syncDisplayBox(); }, 0);
    this._events.timeout(() => { if (!this._editing) this._syncDisplayBox(); }, 400);
    if (!globalThis.ResizeObserver) return;          // older engine: the syncs above stand
    const ro = new ResizeObserver(() => { if (!this._editing) this._syncDisplayBox(); });
    ro.observe(this.el);
    if (host && host !== this.el) ro.observe(host);
    this._events.observe(ro);
  }

  /**
   * ⚠⚠ CALLERS WRITE THESE FIELDS WITH `.val()` AS OFTEN AS WITH `setValue()`.
   * A plain jQuery write goes straight to the element and never reaches this
   * component, so the overlay would keep whatever it last painted - on a freshly
   * loaded screen, nothing.
   *
   * So the element's own `value` property is wrapped per instance: the native
   * setter still does the writing (the value is NEVER rewritten, normalised or
   * clamped here - a display concern must not touch data the screen just wrote),
   * and the paint is refreshed afterwards. `_commit` sets `_writing` around its own
   * assignment because it paints for itself.
   *
   * Why this is safe: the accessor forwards to the native descriptor, so every
   * reader sees exactly the native value; form submission and FormData use the
   * internal value slot, not this property; and `cloneNode` does not copy own
   * properties, so a clone is plain again. `destroy()` deletes it.
   */
  _watchValue() {
    const I = globalThis.HTMLInputElement;
    const d = I && Object.getOwnPropertyDescriptor(I.prototype, 'value');
    if (!d || !d.get || !d.set) return;              // exotic engine: leave the element alone
    const self = this;
    try {
      Object.defineProperty(this.el, 'value', {
        configurable: true,
        enumerable: d.enumerable,
        get() { return d.get.call(this); },
        set(v) { d.set.call(this, v); if (!self._writing && !self._disposed) self._paint(); },
      });
      this._valueWrapped = true;
    } catch (e) { /* a sealed element: the component still works, just not this path */ }
  }

  /**
   * Copy the input's box and type so the overlay text lands exactly on it.
   *
   * ⚠⚠ THE INK MUST BE READ UNMASKED. _paint hides the input's own text with an
   * inline `color: transparent` for as long as this overlay covers it, so a
   * computed read taken while that mask is on returns rgba(0,0,0,0) - and the
   * copy below then paints the overlay in INVISIBLE ink. Both layers are then
   * transparent and the field renders BLANK until it is focused (focus lifts the
   * mask). It is a self-poisoning loop: paint N's mask is what paint N+1 reads,
   * and every re-entry runs with the mask on - _paint's own call below, the two
   * _watchBox re-measures at 0ms and 400ms, the ResizeObserver, and the wrapped
   * `.val()` setter. So the mask is lifted for the duration of the read and put
   * straight back: net-zero mutation, and the ink that reaches the overlay is
   * always the ink the SCREEN gave the field, never a colour this component wrote.
   */
  _syncDisplayBox() {
    const span = this._display$, el = this.el;
    if (!span || !globalThis.getComputedStyle) return;
    const masked = el.style.color === 'transparent';      // set by _paint only; a screen must never author color:transparent on a numberbox
    if (masked) el.style.color = this._inkWas || '';
    const cs = getComputedStyle(el);
    span.style.position = 'absolute';
    span.style.left = el.offsetLeft + 'px';
    span.style.top = el.offsetTop + 'px';
    span.style.width = el.offsetWidth + 'px';
    span.style.height = el.offsetHeight + 'px';
    span.style.font = cs.font;
    span.style.letterSpacing = cs.letterSpacing;
    span.style.textAlign = cs.textAlign;
    span.style.direction = cs.direction;
    span.style.paddingLeft = cs.paddingLeft;
    span.style.paddingRight = cs.paddingRight;
    span.style.borderLeftWidth = cs.borderLeftWidth;
    span.style.borderRightWidth = cs.borderRightWidth;
    span.style.color = cs.color;
    if (masked) el.style.color = 'transparent';          // ⚠ LAST statement: `cs` is live, re-masking earlier re-poisons it
  }

  /** show the grouped text while blurred; get out of the way while editing */
  _paint() {
    const span = this._display$;
    if (!span) return;
    if (this._editing) { span.hidden = true; this.el.style.color = this._inkWas || ''; return; }
    const text = this._display();
    span.textContent = text;
    /* nothing to cover, and an empty overlay must not eat the placeholder */
    if (text === '') { span.hidden = true; this.el.style.color = this._inkWas || ''; return; }
    this._syncDisplayBox();
    span.hidden = false;
    if (this._inkWas === undefined) this._inkWas = this.el.style.color || '';
    this.el.style.color = 'transparent';
  }

  /* ---- input behaviour ---------------------------------------------------- */

  _bindNumeric() {
    const el = this.el;
    this._events.add(el, 'focus', () => {
      this._editing = true;
      this._paint();                                  // reveal the bare number for editing
      /* select-all on focus, opt-in (default off). Deferred a tick so it wins
         over the browser's own click-caret placement, and guarded so a field
         blurred in between is left alone. */
      if (isOn(this.options.selectOnFocus)) {
        this._events.timeout(() => { if (this._editing) { try { el.select(); } catch (e) { /* non-text engine */ } } }, 0);
      }
    });
    this._events.add(el, 'blur', () => {
      this._editing = false;
      this._commit(el.value, false);                  // typed text commits on blur
    });
    this._events.add(el, 'keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();                           // commit IN PLACE, never submit the form
        this._commit(el.value, false);
        return;
      }
      if (!this._allowedKey(e)) e.preventDefault();
    });
  }

  /**
   * Digits always; ONE leading '-', and only where min allows a negative; ONE
   * '.', and only when precision allows decimals. Control keys and shortcuts are
   * untouched. Paste and IME are deliberately NOT filtered - the commit pass
   * cleans them, which is also what lets a grouped paste round-trip.
   */
  _allowedKey(e) {
    if (e.ctrlKey || e.metaKey || e.altKey) return true;
    const k = e.key;
    if (!k || k.length > 1) return true;              // Tab, Backspace, arrows, F-keys...
    if (k >= '0' && k <= '9') return true;
    /* ⚠ The '-' and '.' rules are judged against the text the keystroke will LEAVE,
       never the text it finds: a selected run is REPLACED by the key, so with "12.50"
       fully selected (select-all, or a selectOnFocus field) a typed '.' is not a
       second dot and a typed '-' is a leading minus. Judged against the old text
       (the first build) the field refused the dot until the user had deleted the
       selection by hand - read as "cannot type a decimal". */
    const el = this.el, v = el.value;
    const s = el.selectionStart == null ? v.length : el.selectionStart;
    const t = el.selectionEnd == null ? s : el.selectionEnd;
    const rest = v.slice(0, s) + v.slice(t);
    if (k === '-') {
      const o = this.options;
      const negOk = unset(o.min) || parseFloat(o.min) < 0;
      return negOk && s === 0 && rest.indexOf('-') < 0;
    }
    if (k === '.') return this._precision() > 0 && rest.indexOf('.') < 0;
    return false;
  }

  /* ---- runtime reconfiguration -------------------------------------------- */

  /** a new `value` never ripples; a new rule silently reformats what is held */
  _optionChanged(name, value) {
    if (name === 'onChange') { this._takeOnChange(); return; }   // keep it off the bag - see _takeOnChange
    if (name === 'value') { this._commit(value, true); return; }
    if (name === 'min' || name === 'max' || name === 'precision' ||
        name === 'prefix' || name === 'groupSeparator') {
      if (name === 'precision') this._syncInputmode();
      if (this.el.value !== '') this._commit(this.el.value, true); else this._paint();
      return;
    }
    if (name === 'selectOnFocus') return;             // read at focus time; nothing to rebuild
    super._optionChanged(name, value);                // disabled / readonly -> the family core
  }

  _destroy() {
    if (this._valueWrapped) { try { delete this.el.value; } catch { /* already plain */ } this._valueWrapped = false; }
    if (this._inputmodeAuthored === false) { this.el.removeAttribute('inputmode'); this._inputmodeAuthored = undefined; }
    if (this._display$ && this._display$.parentNode) this._display$.parentNode.removeChild(this._display$);
    this._display$ = null;
    if (this._inkWas !== undefined) { this.el.style.color = this._inkWas; this._inkWas = undefined; }
    if (this._hostPositioned) { this._hostPositioned.style.position = ''; this._hostPositioned = null; }
    this.el.classList.remove('ics-numberbox');
    super._destroy();
  }
}

/* -----------------------------------------------------------------------------
   Numberspinner - the numberbox plus a step. The surface is deliberately small:
   the value contract is the numberbox's, and the only additions are the
   ArrowUp / ArrowDown step, the two chips and two options —
     increment   the step size                              (1)
     spinners    render the up / down chips                 (true)
   spin(up) steps the committed value; an empty field steps from min, else
   from 0. The chips (.ics-numberspinner__spin > __up + __down) are a SIBLING
   of the input, aria-hidden — the keyboard is the accessible path.
   ---------------------------------------------------------------------------- */
export class Numberspinner extends Numberbox {
  static defaults = Object.assign({}, Numberbox.defaults, { increment: 1, spinners: true });

  _init() {
    super._init();
    this.el.classList.add('ics-numberspinner');
    if (isOn(this.options.spinners)) this._buildSpinners();
    this._events.add(this.el, 'keydown', (e) => {
      if (e.key === 'ArrowUp') { e.preventDefault(); this.spin(true); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); this.spin(false); }
    });
  }

  /** step the committed value; an empty field starts from min, else from 0 */
  spin(up) {
    const step = parseFloat(this.options.increment);
    const inc = isNaN(step) ? 1 : step;
    const cur = this._parse(this.el.value);
    const base = cur === null ? (unset(this.options.min) ? 0 : parseFloat(this.options.min)) : cur;
    this._commit(base + (up ? inc : -inc), false);
    return this;
  }

  _buildSpinners() {
    const host = this.el.parentNode;
    if (!host || host.nodeType !== 1) return;
    const box = document.createElement('span');
    box.className = 'ics-numberspinner__spin';
    box.setAttribute('aria-hidden', 'true');
    const up = document.createElement('a');
    up.className = 'ics-numberspinner__up';
    const down = document.createElement('a');
    down.className = 'ics-numberspinner__down';
    box.appendChild(up); box.appendChild(down);
    host.insertBefore(box, this.el.nextSibling);
    this._spin$ = box;
    this._events.add(up, 'click', (e) => { e.preventDefault(); this.spin(true); });
    this._events.add(down, 'click', (e) => { e.preventDefault(); this.spin(false); });
  }

  _destroy() {
    if (this._spin$ && this._spin$.parentNode) this._spin$.parentNode.removeChild(this._spin$);
    this._spin$ = null;
    this.el.classList.remove('ics-numberspinner');
    super._destroy();
  }
}

/* -----------------------------------------------------------------------------
   THE DISPATCH CONTRACT lives HERE, with the component that answers it - not in
   the shell entry, where a re-typed copy drifts. `claimName` cannot carry these
   names: that is CLAIM-IF-FREE, and a page may already hold a handler under
   these plugin names, so each name must be WRAPPED — a receiver this component
   owns reaches the component; any other receiver is passed to the handler
   installed before it, untouched. The entry reads this list and installs one
   dispatcher per row; each row's selector is read back OUT OF THE REGISTRY so
   the mount selector and the dispatch selector can never disagree.
   ---------------------------------------------------------------------------- */
export const NUMBERBOX_DISPATCH = [
  { name: 'numberbox', entry: 'Numberbox' },
  { name: 'numberspinner', entry: 'Numberspinner' },
];

/* Styling rides the family classes (.ics-textbox + the state hooks) plus the
   overlay part in css/components/ics-textbox.css. Selector: <input> only - a grid
   column that names 'numberbox' inside an editor CONFIG STRING is not markup and
   is never matched here. No claimName: the plain names are WRAPPED by the entry
   through NUMBERBOX_DISPATCH, never claimed. */
Registry.add('Numberbox', Numberbox, { selector: 'input[data-ics="numberbox"]' });
Registry.add('Numberspinner', Numberspinner, { selector: 'input[data-ics="numberspinner"]' });
